import React, { useCallback, useEffect, useState } from 'react'
import * as ReactBootstrap from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'

import * as components from '../../components'
import * as rest from '../../services'
import { showErrorToast, showSuccessToast } from '../../state/toasts'
import { SupportPackage, SupportPackageForm } from './SupportPackageForm'

interface Rights {
  has_manager_rights?: boolean
  organization?: string
}

const PRIVILEGED_ROLES = ['SuperUser', 'Privileged']

export const SupportPackagesPage: React.FC = () => {
  const dispatch = useDispatch()
  const { user, is_superuser } = useSelector((state: any) => state.auth)
  const { rights } = useSelector((state: any) => state.userRights)

  const [packages, setPackages] = useState<SupportPackage[] | null>(null)
  const [editing, setEditing] = useState<SupportPackage | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const isPrivileged =
    !!user && (PRIVILEGED_ROLES.includes(user.role) || is_superuser)

  // Update/Delete is allowed for privileged/super users, or the manager of
  // the package's organization. The backend enforces this authoritatively;
  // this only decides whether to show the controls.
  const canModify = (pkg: SupportPackage): boolean =>
    isPrivileged ||
    (rights || []).some(
      (r: Rights) => r.has_manager_rights && r.organization === pkg.organization
    )

  const loadPackages = useCallback(() => {
    setPackages(null)
    new rest.SupportPackageService()
      .makeGetRequest()
      .then((response) => {
        // axios instance already unwraps to response.data (see helpers/backend.ts)
        const data = response as any
        setPackages(Array.isArray(data) ? data : data?.results ?? [])
      })
      .catch((error) => {
        dispatch(
          showErrorToast('Error', error?.response?.data ?? 'Failed to load')
        )
        setPackages([])
      })
  }, [dispatch])

  useEffect(() => {
    loadPackages()
  }, [loadPackages])

  const openCreate = () => {
    setEditing(null)
    setShowForm(true)
  }

  const openEdit = (pkg: SupportPackage) => {
    setEditing(pkg)
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditing(null)
  }

  const handleSubmit = (values: SupportPackage) => {
    setSubmitting(true)
    const service = editing?.url
      ? new rest.SupportPackageService(editing.url)
      : new rest.SupportPackageService()
    const request = editing?.url
      ? service.makePutRequest(values)
      : service.makePostRequest(values)
    request
      .then(() => {
        dispatch(
          showSuccessToast(
            'Success',
            editing ? 'Support package updated.' : 'Support package created.'
          )
        )
        closeForm()
        loadPackages()
      })
      .catch((error) => {
        dispatch(
          showErrorToast('Error', error?.response?.data ?? 'Save failed')
        )
      })
      .finally(() => setSubmitting(false))
  }

  const handleDelete = (pkg: SupportPackage) => {
    if (!pkg.url) {
      return
    }
    new rest.SupportPackageService(pkg.url)
      .makeDeleteRequest()
      .then(() => {
        dispatch(showSuccessToast('Success', 'Support package deleted.'))
        loadPackages()
      })
      .catch((error) => {
        dispatch(
          showErrorToast('Error', error?.response?.data ?? 'Delete failed')
        )
      })
  }

  if (!packages) {
    return <components.Loading />
  }

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Support Packages</h4>
        <ReactBootstrap.Button variant="primary" onClick={openCreate}>
          Add Support Package
        </ReactBootstrap.Button>
      </div>

      <ReactBootstrap.Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Device serial</th>
            <th>IMEI 1</th>
            <th>IMEI 2</th>
            <th>Start date</th>
            <th>End date</th>
            <th>Sold by</th>
            <th>Sold on</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {packages.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center text-muted">
                No support packages found.
              </td>
            </tr>
          ) : (
            packages.map((pkg) => (
              <tr key={pkg.url}>
                <td>{pkg.device_serial_number}</td>
                <td>{pkg.imei1 || '-'}</td>
                <td>{pkg.imei2 || '-'}</td>
                <td>{pkg.start_date}</td>
                <td>{pkg.end_date}</td>
                <td>{pkg.sold_by || '-'}</td>
                <td>{pkg.sold_on || '-'}</td>
                <td>
                  {canModify(pkg) ? (
                    <>
                      <ReactBootstrap.Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => openEdit(pkg)}
                      >
                        Edit
                      </ReactBootstrap.Button>
                      <components.Confirm
                        onConfirm={() => handleDelete(pkg)}
                        body="Are you sure you want to delete this support package?"
                        confirmText="Confirm Delete"
                        confirmBSStyle="danger"
                        title="Delete Support Package"
                      >
                        <ReactBootstrap.Button
                          size="sm"
                          variant="outline-danger"
                        >
                          Delete
                        </ReactBootstrap.Button>
                      </components.Confirm>
                    </>
                  ) : (
                    <span className="text-muted">View only</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </ReactBootstrap.Table>

      <ReactBootstrap.Modal
        show={showForm}
        onHide={closeForm}
        backdrop="static"
      >
        <ReactBootstrap.Modal.Header closeButton>
          <ReactBootstrap.Modal.Title>
            {editing ? 'Edit Support Package' : 'Add Support Package'}
          </ReactBootstrap.Modal.Title>
        </ReactBootstrap.Modal.Header>
        <ReactBootstrap.Modal.Body>
          <SupportPackageForm
            initial={editing ?? undefined}
            submitting={submitting}
            onSubmit={handleSubmit}
            onCancel={closeForm}
          />
        </ReactBootstrap.Modal.Body>
      </ReactBootstrap.Modal>
    </div>
  )
}
